import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { fmtINR } from "../lib/constants";
import { Modal, Input } from "./Leads";
import {
  Plus, ClipboardSignature, Search, FileSpreadsheet, FileText, Download,
  Edit3, Eye, ChevronDown, ChevronRight, ArrowRight, Trash2, Save, ArrowUpDown, Mail,
} from "lucide-react";
import { toast } from "sonner";

const WO_STATUSES = ["Draft", "Approved", "Active", "Closed"];
const STATUS_CLS = {
  Draft: "bg-stone-100 text-stone-700",
  Approved: "bg-amber-100 text-amber-800",
  Active: "bg-emerald-100 text-emerald-800",
  Closed: "bg-stone-200 text-stone-800",
};
const PAGE = 10;

export default function WorkOrders() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ k: "created_at", d: "desc" });
  const [page, setPage] = useState(1);
  const [openEditor, setOpenEditor] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);

  const load = async () => {
    const [w, u, p] = await Promise.all([
      api.get("/work-orders"), api.get("/users"), api.get("/projects"),
    ]);
    setItems(w.data); setUsers(u.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const projByWO = useMemo(() => Object.fromEntries(projects.filter(p => p.work_order_id).map(p => [p.work_order_id, p])), [projects]);
  const userName = (id) => users.find(u => u.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return items.filter(w => {
      if (status && w.status !== status) return false;
      if (!s) return true;
      return [w.wo_number, w.client_name, w.project_name, w.proposal_reference_number].join(" ").toLowerCase().includes(s);
    }).sort((a, b) => {
      const av = (a[sort.k] ?? "").toString().toLowerCase();
      const bv = (b[sort.k] ?? "").toString().toLowerCase();
      return av < bv ? (sort.d === "asc" ? -1 : 1) : av > bv ? (sort.d === "asc" ? 1 : -1) : 0;
    });
  }, [items, search, status, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageItems = filtered.slice((page - 1) * PAGE, page * PAGE);

  const toggleSort = (k) => setSort(s => ({ k, d: s.k === k && s.d === "asc" ? "desc" : "asc" }));

  return (
    <div className="space-y-5 animate-fade-up" style={{ fontFamily: "Poppins, sans-serif" }}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Commercial · Operations</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F2A4A]">Work Orders</h1>
          <p className="text-stone-500 mt-1 text-sm">{items.length} work orders · Auto-fetched from accepted proposals</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`${API}/work-orders/export.csv`} target="_blank" rel="noreferrer" data-testid="wo-export-csv"
             className="flex items-center gap-2 px-3 py-2 bg-white border border-stone-300 hover:border-[#72BE44] rounded-lg text-xs font-medium text-[#0F2A4A] transition">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </a>
          <button onClick={() => { setOpenEditor({}); setViewOnly(false); }} data-testid="new-wo-btn"
                  className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium" style={{ background: "#0F2A4A" }}>
            <Plus className="w-4 h-4" /> New Work Order
          </button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wo-search"
            placeholder="Search by WO no, client, project, proposal ref..."
            className="w-full pl-10 pr-3 py-2 bg-stone-50 border border-transparent rounded-lg focus:bg-white focus:border-[#72BE44] focus:outline-none text-sm transition" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="wo-filter-status"
          className="text-xs px-2.5 py-2 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-[#72BE44]">
          <option value="">All Statuses</option>
          {WO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(search || status) && <button onClick={() => { setSearch(""); setStatus(""); }} className="text-xs text-stone-500 hover:text-rose-600 px-2 py-1">Clear</button>}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
              <tr>
                {[
                  ["wo_number", "WO No."], ["wo_date", "WO Date"],
                  ["client_name", "Client"], ["project_name", "Project"],
                  ["contract_value", "Contract Value", "right"],
                  ["project_lead", "Lead"],
                  ["status", "Project Status"],
                  ["status", "WO Status"],
                ].map(([k, label, align]) => (
                  <th key={label} className={`px-3 py-3 font-semibold cursor-pointer ${align === "right" ? "text-right" : "text-left"}`}
                      onClick={() => toggleSort(k)} data-testid={`wo-sort-${k}`}>
                    <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="w-3 h-3 opacity-60" /></span>
                  </th>
                ))}
                <th className="px-3 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((w) => {
                const proj = projByWO[w.id];
                return (
                  <tr key={w.id} data-testid={`wo-row-${w.id}`} className="border-t border-stone-100 hover:bg-[#72BE44]/5">
                    <td className="px-3 py-2.5 font-mono text-xs text-[#0F2A4A] font-semibold">{w.wo_number}</td>
                    <td className="px-3 py-2.5 text-xs text-stone-600">{w.wo_date || "—"}</td>
                    <td className="px-3 py-2.5 font-medium text-[#0F2A4A]">{w.client_name}</td>
                    <td className="px-3 py-2.5 text-stone-700">{w.project_name}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#0F2A4A]">{fmtINR(w.contract_value)}</td>
                    <td className="px-3 py-2.5 text-xs">{userName(w.project_lead)}</td>
                    <td className="px-3 py-2.5">
                      {proj ? <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">{proj.status || "Active"}</span>
                            : <span className="text-[10px] text-stone-400">— no project —</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${STATUS_CLS[w.status]}`}>{w.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => { setOpenEditor(w); setViewOnly(true); }} title="View" data-testid={`wo-view-${w.id}`}
                          className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A]"><Eye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => { setOpenEditor(w); setViewOnly(false); }} title="Edit" data-testid={`wo-edit-${w.id}`}
                          className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A]"><Edit3 className="w-3.5 h-3.5" /></button>
                        <a href={`${API}/work-orders/${w.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`wo-pdf-${w.id}`}
                           title="Download PDF" className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A]"><Download className="w-3.5 h-3.5" /></a>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-stone-400">
                  <ClipboardSignature className="w-8 h-8 mx-auto mb-2 opacity-40" /> No work orders match
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 text-xs text-stone-600">
          <div>Showing <b>{Math.min((page - 1) * PAGE + 1, filtered.length)}</b>–<b>{Math.min(page * PAGE, filtered.length)}</b> of <b>{filtered.length}</b></div>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-2 py-1 rounded hover:bg-stone-100 disabled:opacity-30">‹</button>
            <span className="px-3 py-1 bg-stone-100 rounded font-medium">{page} / {pages}</span>
            <button disabled={page === pages} onClick={() => setPage(page + 1)} className="px-2 py-1 rounded hover:bg-stone-100 disabled:opacity-30">›</button>
          </div>
        </div>
      </div>

      {openEditor && (
        <WOEditor wo={openEditor} users={users} readOnly={viewOnly}
          onClose={() => setOpenEditor(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function WOEditor({ wo, users, readOnly, onClose, onSaved }) {
  const isNew = !wo.id;
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [w, setW] = useState({
    client_name: "", project_name: "", project_location: "", contract_value: 0, gst_percent: 18,
    payment_terms: "", milestone_billing: [], advance_amount: 0, retention_amount: 0,
    scope_of_work: "", inclusions: "", exclusions: "", assumptions: "",
    deliverables: [], client_responsibilities: [], pt_responsibilities: [],
    additional_terms: [], attachments: [],
    project_lead: "", associate: "", reviewer: "", hod: "",
    status: "Draft", ...wo,
  });
  const upd = (patch) => setW((s) => ({ ...s, ...patch }));
  const [sec, setSec] = useState({ basic: true, commercial: true, scope: false, terms: false, attach: false, intl: false });
  const toggle = (k) => setSec((s) => ({ ...s, [k]: !s[k] }));
  const calc = useMemo(() => {
    const cv = Number(w.contract_value) || 0;
    const gst = +(cv * (Number(w.gst_percent) || 0) / 100).toFixed(2);
    return { cv, gst, total: +(cv + gst).toFixed(2) };
  }, [w.contract_value, w.gst_percent]);

  const save = async () => {
    try {
      if (isNew) {
        const { data } = await api.post("/work-orders", w);
        toast.success(`Created ${data.wo_number}`);
        onSaved();
        onClose();
      } else {
        await api.put(`/work-orders/${w.id}`, w);
        toast.success("Saved");
        onSaved();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const convertToProject = async () => {
    if (!confirmConvert) {
      setConfirmConvert(true);
      setTimeout(() => setConfirmConvert(false), 5000);
      return;
    }
    setConfirmConvert(false);
    try {
      const { data } = await api.post(`/work-orders/${w.id}/convert-to-project`, {
        service_type: "Fresh EC",
        service_category: "Environmental Clearance",
      });
      toast.success(`Project ${data.project_id_external} created — charter, timeline, folders & KYC auto-loaded`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [emailing, setEmailing] = useState(false);
  const emailWO = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/emails/work-order/${w.id}`);
      toast.success(`Work order emailed to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setEmailing(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-stone-50 w-full max-w-5xl h-full overflow-y-auto shadow-2xl animate-slide-in" onClick={(e) => e.stopPropagation()} data-testid="wo-editor">
        <div className="sticky top-0 bg-[#0F2A4A] text-white p-5 z-10 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#72BE44] font-bold">Work Order</div>
            <div className="font-mono text-sm mt-1">{w.wo_number || "— New —"}</div>
            <h2 className="text-xl font-bold mt-1">{w.project_name || "New Work Order"}</h2>
            <div className="text-xs text-slate-300 mt-1">{w.client_name}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {w.status && <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-bold ${STATUS_CLS[w.status]}`}>{w.status}</span>}
            <button onClick={onClose} className="text-xs text-white/70 hover:text-white px-2 py-1">Close</button>
          </div>
        </div>

        <div className="bg-white border-b border-stone-200 px-5 py-3 flex items-center gap-2 flex-wrap sticky top-[120px] z-[5]">
          {!readOnly && <button onClick={save} data-testid="wo-save" className="flex items-center gap-1.5 bg-[#72BE44] text-white px-3 py-1.5 rounded text-xs font-medium"><Save className="w-3.5 h-3.5" /> Save</button>}
          {!isNew && <a href={`${API}/work-orders/${w.id}/pdf`} target="_blank" rel="noreferrer" data-testid="wo-pdf-download"
            className="flex items-center gap-1.5 bg-stone-200 text-[#0F2A4A] px-3 py-1.5 rounded text-xs font-medium"><Download className="w-3.5 h-3.5" /> Download PDF</a>}
          {!isNew && (
            <button onClick={emailWO} disabled={emailing} data-testid="wo-email-btn"
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-60">
              <Mail className="w-3.5 h-3.5" /> {emailing ? "Sending..." : "Email to Client"}</button>
          )}
          {!readOnly && !isNew && !w.project_id && (
            <button onClick={convertToProject} data-testid="wo-create-project"
              className={`flex items-center gap-1.5 text-white px-3 py-1.5 rounded text-xs font-medium transition ${confirmConvert ? "bg-rose-600 hover:bg-rose-700" : "bg-[#0F2A4A]"}`}>
              <ArrowRight className="w-3.5 h-3.5" /> {confirmConvert ? "Click again to confirm — creates Charter, Checklist & Timeline" : "Create Project"}</button>
          )}
          {w.project_id && <span className="text-[11px] text-stone-500 italic">Project linked</span>}
        </div>

        <div className="p-5 space-y-4">
          <Card title="A. Basic Details" open={sec.basic} onToggle={() => toggle("basic")}>
            <div className="grid grid-cols-2 gap-3">
              <ED label="WO Number" value={w.wo_number} onChange={(v) => upd({ wo_number: v })} ro={readOnly} testid="wo-number-input" />
              <ED label="WO Date" type="date" value={w.wo_date} onChange={(v) => upd({ wo_date: v })} ro={readOnly} testid="wo-date" />
              <ED label="Client Name" value={w.client_name} onChange={(v) => upd({ client_name: v })} ro={readOnly} testid="wo-client" />
              <ED label="Project Name" value={w.project_name} onChange={(v) => upd({ project_name: v })} ro={readOnly} testid="wo-project" />
              <ED label="Project Location" value={w.project_location} onChange={(v) => upd({ project_location: v })} ro={readOnly} testid="wo-location" />
              <ED label="Proposal Ref." value={w.proposal_reference_number} onChange={(v) => upd({ proposal_reference_number: v })} ro={readOnly} testid="wo-prop-ref" />
              <ED label="Proposal Date" type="date" value={w.proposal_date} onChange={(v) => upd({ proposal_date: v })} ro={readOnly} testid="wo-prop-date" />
              <ED label="Contact Person" value={w.client_contact_person} onChange={(v) => upd({ client_contact_person: v })} ro={readOnly} testid="wo-contact" />
              <ED label="Client Mobile" value={w.client_phone} onChange={(v) => upd({ client_phone: v })} ro={readOnly} testid="wo-phone" />
              <ED label="Client Email" type="email" value={w.client_email} onChange={(v) => upd({ client_email: v })} ro={readOnly} testid="wo-email" />
            </div>
          </Card>

          <Card title="B. Commercial Details" open={sec.commercial} onToggle={() => toggle("commercial")}>
            <div className="grid grid-cols-3 gap-3">
              <ED label="Contract Value (₹)" type="number" value={w.contract_value} onChange={(v) => upd({ contract_value: Number(v) })} ro={readOnly} testid="wo-cv" />
              <ED label="GST %" type="number" value={w.gst_percent} onChange={(v) => upd({ gst_percent: Number(v) })} ro={readOnly} testid="wo-gst" />
              <div>
                <Label>Total Value (auto)</Label>
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-sm font-bold text-[#0F2A4A]">
                  ₹ {calc.total.toLocaleString("en-IN")} <span className="text-xs text-stone-500 font-normal">(GST ₹{calc.gst.toLocaleString("en-IN")})</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <ED label="Advance Amount (₹)" type="number" value={w.advance_amount} onChange={(v) => upd({ advance_amount: Number(v) })} ro={readOnly} testid="wo-advance" />
              <ED label="Retention Amount (₹)" type="number" value={w.retention_amount} onChange={(v) => upd({ retention_amount: Number(v) })} ro={readOnly} testid="wo-retention" />
            </div>
            <ED label="Payment Terms" textarea rows={2} value={w.payment_terms} onChange={(v) => upd({ payment_terms: v })} ro={readOnly} testid="wo-pterms" />

            <Label>Milestone Billing Structure</Label>
            <table className="w-full text-xs border border-stone-200 rounded">
              <thead className="bg-[#0F2A4A] text-white">
                <tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Label / Trigger</th><th className="px-2 py-1 text-left">%</th><th className="px-2 py-1 text-right">Amount</th>{!readOnly && <th className="w-8"></th>}</tr>
              </thead>
              <tbody>
                {(w.milestone_billing || []).map((m, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="px-2 py-1 text-stone-500">{i + 1}</td>
                    <td className="p-1">{readOnly ? <span>{m.label}</span> :
                      <input value={m.label || ""} onChange={(e) => { const ms = [...w.milestone_billing]; ms[i] = { ...ms[i], label: e.target.value }; upd({ milestone_billing: ms }); }}
                        className="w-full px-1 py-0.5 border-0 bg-stone-50 rounded text-xs focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#72BE44]" />}</td>
                    <td className="p-1 w-16">{readOnly ? <span>{m.percent}</span> :
                      <input type="number" value={m.percent ?? ""} onChange={(e) => {
                        const ms = [...w.milestone_billing]; ms[i] = { ...ms[i], percent: Number(e.target.value), amount: +(calc.cv * Number(e.target.value) / 100).toFixed(2) }; upd({ milestone_billing: ms });
                      }} className="w-full px-1 py-0.5 bg-stone-50 rounded text-xs" />}</td>
                    <td className="p-1 text-right">{readOnly ? <span>{m.amount?.toLocaleString("en-IN")}</span> :
                      <input type="number" value={m.amount ?? 0} onChange={(e) => { const ms = [...w.milestone_billing]; ms[i] = { ...ms[i], amount: Number(e.target.value) }; upd({ milestone_billing: ms }); }}
                        className="w-28 px-2 py-0.5 bg-stone-50 rounded text-xs text-right" />}</td>
                    {!readOnly && <td className="p-1"><button onClick={() => upd({ milestone_billing: w.milestone_billing.filter((_, x) => x !== i) })} className="text-rose-500"><Trash2 className="w-3 h-3" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {!readOnly && <button onClick={() => upd({ milestone_billing: [...(w.milestone_billing || []), { label: "", percent: 0, amount: 0 }] })} className="mt-2 text-xs text-[#72BE44] hover:underline">+ Add Milestone</button>}
          </Card>

          <Card title="C. Scope Details (auto-fetched from proposal)" open={sec.scope} onToggle={() => toggle("scope")}>
            <ED label="Scope of Work" textarea rows={8} value={w.scope_of_work} onChange={(v) => upd({ scope_of_work: v })} ro={readOnly} testid="wo-scope" />
            <div className="grid grid-cols-2 gap-3">
              <ED label="Inclusions" textarea rows={4} value={w.inclusions} onChange={(v) => upd({ inclusions: v })} ro={readOnly} testid="wo-inc" />
              <ED label="Exclusions" textarea rows={4} value={w.exclusions} onChange={(v) => upd({ exclusions: v })} ro={readOnly} testid="wo-exc" />
            </div>
            <ED label="Assumptions" textarea rows={3} value={w.assumptions} onChange={(v) => upd({ assumptions: v })} ro={readOnly} testid="wo-ass" />
            <ED label="Deliverables (one per line)" textarea rows={4} value={(w.deliverables || []).join("\n")}
              onChange={(v) => upd({ deliverables: v.split("\n").map(s => s.trim()).filter(Boolean) })} ro={readOnly} testid="wo-deliv" />
            <div className="grid grid-cols-2 gap-3">
              <ED label="Client Responsibilities (one per line)" textarea rows={4} value={(w.client_responsibilities || []).join("\n")}
                onChange={(v) => upd({ client_responsibilities: v.split("\n").map(s => s.trim()).filter(Boolean) })} ro={readOnly} testid="wo-cresp" />
              <ED label="PT Responsibilities (one per line)" textarea rows={4} value={(w.pt_responsibilities || []).join("\n")}
                onChange={(v) => upd({ pt_responsibilities: v.split("\n").map(s => s.trim()).filter(Boolean) })} ro={readOnly} testid="wo-ptresp" />
            </div>
          </Card>

          <Card title="D. Terms & Conditions" open={sec.terms} onToggle={() => toggle("terms")}>
            {(w.additional_terms || []).map((t, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <span className="text-stone-500 text-xs pt-1.5 w-6">{i + 1}.</span>
                {readOnly ? <p className="flex-1 text-sm text-stone-700">{t}</p> : <>
                  <textarea value={t} rows={2} onChange={(e) => { const ts = [...w.additional_terms]; ts[i] = e.target.value; upd({ additional_terms: ts }); }}
                    className="flex-1 px-2 py-1 bg-white border border-stone-200 rounded text-xs" />
                  <button onClick={() => upd({ additional_terms: w.additional_terms.filter((_, x) => x !== i) })} className="text-rose-500 mt-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </>}
              </div>
            ))}
            {!readOnly && <button onClick={() => upd({ additional_terms: [...(w.additional_terms || []), ""] })} className="text-xs text-[#72BE44] hover:underline">+ Add Term</button>}
          </Card>

          <Card title="E. Work Order Attachments" open={sec.attach} onToggle={() => toggle("attach")}>
            {(w.attachments || []).map((a, i) => (
              <div key={i} className="flex items-center gap-2 mb-2 text-sm">
                {readOnly ? (<><span className="font-medium text-[#0F2A4A]">{a.name}</span><a href={a.url} target="_blank" rel="noreferrer" className="text-[#72BE44] underline text-xs">{a.url}</a></>) : (<>
                  <input value={a.name || ""} onChange={(e) => { const at = [...w.attachments]; at[i] = { ...at[i], name: e.target.value }; upd({ attachments: at }); }}
                    placeholder="Name" className="flex-1 px-2 py-1 bg-white border border-stone-200 rounded text-xs" />
                  <input value={a.url || ""} onChange={(e) => { const at = [...w.attachments]; at[i] = { ...at[i], url: e.target.value }; upd({ attachments: at }); }}
                    placeholder="URL (signed WO, PO, NDA…)" className="flex-1 px-2 py-1 bg-white border border-stone-200 rounded text-xs" />
                  <button onClick={() => upd({ attachments: w.attachments.filter((_, x) => x !== i) })} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </>)}
              </div>
            ))}
            {!readOnly && <button onClick={() => upd({ attachments: [...(w.attachments || []), { name: "", url: "" }] })} className="text-xs text-[#72BE44] hover:underline">+ Add Attachment</button>}
            <p className="text-xs text-stone-500 mt-2 italic">Recommended: Signed Work Order, Purchase Order, Agreement, NDA, Supporting Documents.</p>
          </Card>

          <Card title="F. Internal Allocation" open={sec.intl} onToggle={() => toggle("intl")}>
            <div className="grid grid-cols-2 gap-3">
              <SelField label="Project Lead" value={w.project_lead} onChange={(v) => upd({ project_lead: v })} users={users} ro={readOnly} testid="wo-lead" />
              <SelField label="Associate" value={w.associate} onChange={(v) => upd({ associate: v })} users={users} ro={readOnly} testid="wo-associate" />
              <SelField label="Reviewer" value={w.reviewer} onChange={(v) => upd({ reviewer: v })} users={users} ro={readOnly} testid="wo-reviewer" />
              <SelField label="HOD" value={w.hod} onChange={(v) => upd({ hod: v })} users={users} ro={readOnly} testid="wo-hod" />
            </div>
            <div className="mt-3">
              <Label>WO Status</Label>
              {readOnly ? <div className="text-sm">{w.status}</div> :
                <select value={w.status} onChange={(e) => upd({ status: e.target.value })} data-testid="wo-status-input"
                  className="px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]">
                  {WO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, open, onToggle, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 hover:bg-stone-100 transition">
        <span className="font-semibold text-[#0F2A4A] text-sm">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold block mt-2 mb-1">{children}</label>;
}

function ED({ label, value, onChange, ro, textarea, rows = 2, type = "text", testid }) {
  if (ro) {
    return (
      <div>
        {label && <Label>{label}</Label>}
        <div className="px-3 py-2 bg-stone-50 border border-stone-100 rounded text-sm text-stone-700 whitespace-pre-wrap">{value || "—"}</div>
      </div>
    );
  }
  return (
    <div>
      {label && <Label>{label}</Label>}
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
          className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]" />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]" />
      )}
    </div>
  );
}

function SelField({ label, value, onChange, users, ro, testid }) {
  if (ro) return <div><Label>{label}</Label><div className="text-sm text-stone-700">{users.find(u => u.id === value)?.name || "—"}</div></div>;
  return (
    <div>
      <Label>{label}</Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]">
        <option value="">— Unassigned —</option>
        {users.filter(u => u.role !== "client").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
    </div>
  );
}
